import { useCallback, type ReactNode } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Code, ConnectError } from "@connectrpc/connect";
import { SaasProvider, useBrand } from "@fox1ck-org/auth/saas/react";
import { SaasGate, type GateAccess, type GateProduct } from "@fox1ck-org/auth/ui";
import { BrandTile } from "@fox1ck-org/ui";

import { PRODUCT, consoleTransport, fastLogin, runtimeConfig } from "@/lib/auth";
import { getLocale } from "@/lib/i18n";
import { useCaller } from "@/lib/permissions";

/**
 * Every signed-in route sits under this layout route: realm `customers`,
 * console's org half (`SaasProvider`), and the package's gate (card А1):
 * zero-input sign-in for orgs with SSO (S16 — our staff land through org
 * «Vibe»'s IdP), «Вы вышли», the org picker, «Трекер не подключён», a
 * suspended org, the session's end over the page and the `?account=` window
 * are all `SaasGate`'s, drawn by `@fox1ck-org/ui/saas`. The tracker adds one
 * thing: what the person may do here is the API's answer (`GetCaller`) — the
 * gate's `access`, asked only once the person is signed in to an org that has
 * the tracker.
 */
export function SignedInArea() {
  const navigate = useNavigate();
  const go = useCallback((to: string) => void navigate(to, { replace: true }), [navigate]);
  return (
    <SaasProvider consoleUrl={runtimeConfig.consoleUrl} transport={consoleTransport}>
      <Gate navigate={go}>
        <Outlet />
      </Gate>
    </SaasProvider>
  );
}

function Gate({ navigate, children }: { navigate: (to: string) => void; children: ReactNode }) {
  const product = useBrandedProduct();
  return (
    <SaasGate product={product} fastLogin={fastLogin} access={useTrackerAccess} navigate={navigate} locale={getLocale()}>
      {children}
    </SaasGate>
  );
}

/**
 * The tracker's own «may I»: GetCaller. Refused — no role here; any other
 * failure — the gate's «нет связи» with a retry; an answer — in.
 */
function useTrackerAccess(): GateAccess {
  const caller = useCaller();
  if (caller.isPending) return { state: "loading" };
  if (caller.isError) {
    const code = caller.error instanceof ConnectError ? caller.error.code : undefined;
    if (code === Code.PermissionDenied) return { state: "denied" };
    return { state: "error", retry: () => void caller.refetch() };
  }
  return { state: "allowed" };
}

/**
 * The product's name and mark (card S11b, spec ui/brands «Product identity»):
 * console's brand — by the host before the first frame (`loadBrand` in
 * main.tsx), then as the signed-in org sees this host — its title («Vibe ·
 * Трекер» in ecosystem, the product's own brand in standalone, the customer's
 * under a white-label) and the product's mark on the brand's tile (a
 * customer's uploaded logo in its place). No title: the brand's name, then the
 * module title, then the catalog id — the product never writes its own name
 * (board TEdges). The tab's icon is auth's (`applyBrandIcon`), its name the
 * shell's (`useScreenTitle`).
 */
function useBrandedProduct(): GateProduct {
  const { title, name, moduleTitle, logoUrl, mark } = useBrand();
  return {
    id: PRODUCT,
    name: title ?? name ?? (moduleTitle || PRODUCT),
    mark: logoUrl ? <img src={logoUrl} alt="" className="size-full rounded-[inherit] object-contain" data-testid="brand-logo" /> : <BrandTile mark={mark} />,
  };
}
